/**
 * EL CENTRO Y SUS SECCIONES
 *
 * Resumen, Planificación, Importaciones, Órdenes de trabajo y Calendario.
 * Son pestañas porque cambiar entre ellas tiene que ser inmediato.
 *
 * La cabecera del centro se carga una vez en DisposicionCentro y la
 * comparten todas las pestañas a través del contexto de outlet.
 * `recargarCentro` viaja en el mismo contexto para que ResumenCentro
 * pueda refrescar el título y la miga después de editar.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';

import { api, type Centro, type CentroDetalle, FalloApi } from '../api.ts';
import {
  Avatar,
  CampoTexto,
  DiasOperativosInput,
  Cargando,
  Error as ErrorCaja,
  useDatos,
} from '../componentes/base.tsx';
import { BloqueosCentro } from '../componentes/bloqueos-centro.tsx';
import { FestivosCentro } from '../componentes/festivos-centro.tsx';
import { PlanificacionResumen } from '../componentes/planificacion-resumen.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';

export interface ContextoCentro {
  readonly centro: Centro;
  readonly recargarCentro: () => void;
  /**
   * El botón «Editar centro» vive en la cabecera (`DisposicionCentro`,
   * compartida por todas las pestañas), pero el formulario sigue
   * pintándose exactamente donde estaba (`ResumenCentro`, sólo en la
   * pestaña Resumen): cambia el punto de acceso, no el flujo. Este par de
   * estado viaja por el contexto del outlet para que los dos lo compartan
   * sin crear estado global.
   */
  readonly editandoCentro: boolean;
  readonly setEditandoCentro: (v: boolean) => void;
}

export function useCentro(): ContextoCentro {
  return useOutletContext<ContextoCentro>();
}

const SECCIONES = [
  { a: '', texto: 'Resumen', fin: true },
  { a: 'planificacion', texto: 'Planificación', fin: false },
  { a: 'importaciones', texto: 'Importaciones', fin: false },
  { a: 'ots', texto: 'Órdenes de trabajo', fin: false },
  //  FASE 6.4: «Capacidad y carga» — nombre distinto a propósito, porque
  //  «Planificación» ya es la pestaña de planes de limpieza (arriba).
  { a: 'capacidad', texto: 'Capacidad y carga', fin: false },
  { a: 'calendario', texto: 'Calendario', fin: false },
  { a: 'ubicaciones', texto: 'Ubicaciones', fin: false },
] as const;

export function DisposicionCentro(): ReactNode {
  const { idCentro = '' } = useParams();
  const { puede } = useSesion();
  const location = useLocation();
  const estado = useDatos(() => api.get<Centro>(`/api/centros/${idCentro}`), [idCentro]);
  const [editandoCentro, setEditandoCentro] = useState(false);

  if (estado.cargando && estado.datos === null) {
    return (
      <Shell titulo="Centro">
        <Cargando />
      </Shell>
    );
  }
  if (estado.error !== null || estado.datos === null) {
    return (
      <Shell titulo="Centro">
        <ErrorCaja error={estado.error} reintentar={estado.recargar} />
      </Shell>
    );
  }

  const centro = estado.datos;
  //  `end` de la pestaña Resumen: mismo criterio que usa su `NavLink` más
  //  abajo (`s.fin`). El botón sólo tiene sentido donde el formulario
  //  realmente aparece.
  const enResumen = location.pathname === `/centros/${idCentro}`;

  return (
    <Shell
      migas={[
        { texto: 'Clientes', a: '/clientes' },
        { texto: centro.clienteRazonSocial, a: `/clientes/${centro.idCliente}` },
        { texto: centro.descripcion },
      ]}
      titulo={
        <span className="fila" style={{ gap: 10 }}>
          <Avatar nombre={centro.clienteRazonSocial} logoUrl={centro.clienteLogoUrl} pequeno />
          <span>{centro.descripcion}</span>
          <span className={`etiqueta ${centro.activo ? 'exito' : 'neutra'}`}>
            {centro.activo ? 'ACTIVO' : 'INACTIVO'}
          </span>
        </span>
      }
      subtitulo={
        <>
          <Link to={`/clientes/${centro.idCliente}`}>{centro.clienteRazonSocial}</Link>
          <span aria-hidden="true" className="tenue">·</span>
          <span className="mono">{centro.codigo}</span>
        </>
      }
      acciones={
        enResumen && puede('centros:editar') ? (
          <button onClick={() => setEditandoCentro((v) => !v)}>
            {editandoCentro ? 'Cancelar' : 'Editar centro'}
          </button>
        ) : undefined
      }
      tabs={
        <nav className="tabs" aria-label="Secciones del centro">
          {SECCIONES.map((s) => (
            <NavLink
              key={s.a}
              to={s.a === '' ? `/centros/${idCentro}` : `/centros/${idCentro}/${s.a}`}
              end={s.fin}
              className={({ isActive }) => (isActive ? 'activo' : '')}
            >
              {s.texto}
            </NavLink>
          ))}
        </nav>
      }
    >
      <Outlet
        context={
          { centro, recargarCentro: estado.recargar, editandoCentro, setEditandoCentro } satisfies ContextoCentro
        }
      />
    </Shell>
  );
}

// ------------------------------------------------------------------ resumen

export function ResumenCentro(): ReactNode {
  const { centro, recargarCentro, editandoCentro, setEditandoCentro } = useCentro();

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Datos del centro</h2>
      </div>
      {editandoCentro ? (
        <FormEditarCentro
          idCentro={centro.id}
          alEditar={() => {
            setEditandoCentro(false);
            recargarCentro();
          }}
          alCancelar={() => setEditandoCentro(false)}
        />
      ) : (
        <div className="panel-cuerpo">
          <div className="datos">
            <div className="dato">
              <div className="etiqueta-dato">Cliente</div>
              <div className="valor-dato">
                <Link to={`/clientes/${centro.idCliente}`}>{centro.clienteRazonSocial}</Link>
              </div>
            </div>
            <Dato etiqueta="Código de cliente" valor={centro.clienteCodigo} mono />
            <Dato etiqueta="Centro" valor={centro.descripcion} />
            <Dato etiqueta="Código de centro" valor={centro.codigo} mono />
            <Dato
              etiqueta="Días operativos"
              valor={
                centro.diasOperativos === null
                  ? 'Todos (sin patrón declarado)'
                  : centro.diasOperativos.length === 0
                    ? 'Sin días operativos'
                    : etiquetaDias(centro.diasOperativos)
              }
            />
            <div className="dato">
              <div className="etiqueta-dato">Estado</div>
              <div className="valor-dato">
                <span className={`etiqueta ${centro.activo ? 'exito' : 'neutra'}`}>
                  {centro.activo ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dato({
  etiqueta,
  valor,
  mono = false,
}: {
  etiqueta: string;
  valor: string;
  mono?: boolean;
}): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{etiqueta}</div>
      <div className={`valor-dato${mono ? ' mono' : ''}`}>{valor}</div>
    </div>
  );
}

// ---------------------------------------------------------------- formulario de edición de centro

function FormEditarCentro({
  idCentro,
  alEditar,
  alCancelar,
}: {
  idCentro: string;
  alEditar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const detalle = useDatos(
    () => api.get<CentroDetalle>(`/api/centros/${idCentro}/detalle`),
    [idCentro],
  );

  if (detalle.cargando && detalle.datos === null)
    return (
      <div className="form-inline">
        <Cargando />
      </div>
    );
  if (detalle.error !== null || detalle.datos === null)
    return (
      <div className="form-inline">
        <ErrorCaja error={detalle.error} reintentar={detalle.recargar} />
      </div>
    );

  return (
    <FormEditarCentroConDatos
      datos={detalle.datos}
      idCentro={idCentro}
      alEditar={alEditar}
      alCancelar={alCancelar}
    />
  );
}

function FormEditarCentroConDatos({
  datos,
  idCentro,
  alEditar,
  alCancelar,
}: {
  datos: CentroDetalle;
  idCentro: string;
  alEditar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [descripcion, setDescripcion] = useState(datos.descripcion);
  const [direccion, setDireccion] = useState(datos.direccion ?? '');
  const [activo, setActivo] = useState(datos.activo);
  const [diasOperativos, setDiasOperativos] = useState<number[] | null>(datos.diasOperativos);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.patch<CentroDetalle>(`/api/centros/${idCentro}`, {
        descripcion,
        direccion: direccion.trim() || null,
        activo,
        diasOperativos,
      });
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label>Código</label>
          <input type="text" value={datos.codigo} disabled />
        </div>
        <CampoTexto label="Descripción" value={descripcion} onChange={setDescripcion} error={camposMal['descripcion']} disabled={enviando} required />
        <CampoTexto label="Dirección" value={direccion} onChange={setDireccion} error={camposMal['direccion']} disabled={enviando} />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label>Días operativos</label>
        <DiasOperativosInput value={diasOperativos} onChange={setDiasOperativos} disabled={enviando} />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label style={{ textTransform: 'none', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--texto)', fontWeight: 'normal', marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            disabled={enviando}
          />
          Centro activo
        </label>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------- capacidad y carga

/** FASE 6.4: resumen de planificación — capacidad, carga, conflictos, alertas. */
export function PaginaCapacidadCarga(): ReactNode {
  const { centro } = useCentro();
  return (
    <>
      {/* FASE 6.6 §S: acceso contextual, sin duplicar la implementación del planner. */}
      <div className="fila" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        <Link className="enlace" to={`/planner?idCliente=${centro.idCliente}&idCentro=${centro.id}`}>Ver en Planner →</Link>
      </div>
      <PlanificacionResumen idCentro={centro.id} />
    </>
  );
}

// ---------------------------------------------------------------- calendario

/**
 * FASE 6.3: calendario anual de festivos + bloqueos operativos. El
 * listado tabular de excepciones (FASE 3.x) se sustituye por la
 * cuadrícula anual de `FestivosCentro` — misma API, misma semántica de
 * permisos, presentación nueva.
 */
export function PaginaCalendario(): ReactNode {
  const { centro } = useCentro();
  const { puede } = useSesion();

  return (
    <>
      <FestivosCentro idCentro={centro.id} puedeEditar={puede('calendario:editar')} />
      <BloqueosCentro idCentro={centro.id} puedeGestionar={puede('bloqueos:gestionar')} />
    </>
  );
}

// --------------------------------------------------------------- utilidades

const ABREV_DIA = ['', 'L', 'M', 'X', 'J', 'V', 'S', 'D'];

function etiquetaDias(dias: number[]): string {
  return dias
    .slice()
    .sort((a, b) => a - b)
    .map((d) => ABREV_DIA[d] ?? String(d))
    .join(' ');
}
